/**
 * On-device diagnostics for the screens we cannot reproduce here (an iPad's Safari, mostly).
 *
 * Open the app with `?debug=1` once (or `?debug=0` to turn it off): the choice is remembered on
 * the device. While it is on, the audio service, the runner and the minigames write timestamped
 * lines into a ring buffer, page errors and unhandled rejections are captured, a small bug
 * button opens a panel that shows and copies the log, and an on-device console (Eruda) is
 * loaded for network and element inspection. Nothing here runs when the flag is off.
 */

const KEY = 'ezishizi:debug';
const LIMIT = 400;

export interface DiagLine {
  /** Milliseconds since the page loaded. */
  t: number;
  tag: string;
  msg: string;
}

const lines: DiagLine[] = [];
const listeners = new Set<() => void>();
let enabled = false;
let installed = false;

function read(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Apply `?debug=1|0` from the URL (remembered), then report whether diagnostics are on. */
export function initDiag(): boolean {
  try {
    const v = new URLSearchParams(location.search).get('debug');
    if (v === '1') localStorage.setItem(KEY, '1');
    if (v === '0') localStorage.removeItem(KEY);
  } catch {
    /* no storage: this visit only */
  }
  enabled = read();
  if (enabled && !installed) {
    installed = true;
    window.addEventListener('error', (e) =>
      log('error', `${e.message} @ ${e.filename ?? '?'}:${e.lineno ?? '?'}`),
    );
    window.addEventListener('unhandledrejection', (e) => {
      const r = e.reason as { name?: string; message?: string } | undefined;
      log('rejection', r?.message ? `${r.name ?? 'Error'}: ${r.message}` : String(e.reason));
    });
    log('diag', `on · ${navigator.userAgent}`);
    log('diag', `viewport ${innerWidth}×${innerHeight} · dpr ${devicePixelRatio}`);
    void loadConsole();
  }
  return enabled;
}

export const diagEnabled = () => enabled;

const t0 = typeof performance !== 'undefined' ? performance.now() : 0;

/** Record one line. Cheap when diagnostics are off (a boolean check). */
export function log(tag: string, msg: string): void {
  if (!enabled) return;
  const t = Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - t0);
  lines.push({ t, tag, msg });
  if (lines.length > LIMIT) lines.splice(0, lines.length - LIMIT);
  for (const l of listeners) l();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getLines(): readonly DiagLine[] {
  return lines;
}

export function clearLines(): void {
  lines.length = 0;
  for (const l of listeners) l();
}

const fmt = (ms: number) => `${(ms / 1000).toFixed(2).padStart(7)}s`;

/** The whole log as text, ready to paste. */
export function dump(): string {
  const head = [
    `Ezi Shizi diagnostics · ${new Date().toISOString()}`,
    `URL ${location.href}`,
    `UA ${navigator.userAgent}`,
    '',
  ];
  return head.concat(lines.map((l) => `${fmt(l.t)}  ${l.tag.padEnd(9)} ${l.msg}`)).join('\n');
}

/** Eruda: a console, network and element inspector that runs on the device itself. */
let consoleLoading: Promise<void> | null = null;
export function loadConsole(): Promise<void> {
  if (consoleLoading) return consoleLoading;
  consoleLoading = new Promise<void>((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/eruda@3.4.1/eruda.min.js';
    s.async = true;
    s.onload = () => {
      try {
        (window as unknown as { eruda?: { init(): void } }).eruda?.init();
        log('diag', 'console ready (Eruda)');
      } catch (e) {
        log('diag', `console failed: ${String(e)}`);
      }
      resolve();
    };
    s.onerror = () => {
      log('diag', 'console script failed to load');
      resolve();
    };
    document.head.appendChild(s);
  });
  return consoleLoading;
}

export function showConsole(): void {
  const eruda = (window as unknown as { eruda?: { show(): void } }).eruda;
  if (eruda) eruda.show();
  else void loadConsole().then(showConsole);
}
